import type {
  Feedback,
  FeedbackAutomationConfig,
  FeedbackAutomationEvent,
  FeedbackAutomationEventOnlyState,
  FeedbackAutomationState,
  FeedbackConfidenceSignal,
  FeedbackStatus,
} from '../api/types';

export type ConfidenceLevel = 'High' | 'Medium' | 'Low';

/** High from 75, Medium from 50, Low below. */
export function confidenceLevel(score: number): ConfidenceLevel {
  if (score >= 75) return 'High';
  if (score >= 50) return 'Medium';
  return 'Low';
}

export function confidenceBadgeVariant(
  level: ConfidenceLevel
): 'default' | 'secondary' | 'outline' {
  if (level === 'High') return 'default';
  if (level === 'Medium') return 'secondary';
  return 'outline';
}

const SIGNAL_TEXT: Record<string, string> = {
  intent: 'The user said what they wanted to do',
  observed_expected: 'What happened and what should have happened are both described',
  steps: 'Steps to reproduce are listed',
  user_confirmed: 'The user confirmed the summary',
  open_questions: 'Questions are still open',
  abandoned: 'The user left before the conversation finished',
  replay_route_match: 'The replay shows the page the user reported',
  replay_errors: 'The replay recorded errors',
  replay_rage_clicks: 'The replay shows repeated angry clicks',
  replay_reloads: 'The replay shows the user reloading the page',
  replay_unavailable: 'No replay was recorded for this conversation',
};

/** Plain words for one confidence signal; unknown names from a newer Rudi still read. */
export function signalText(signal: FeedbackConfidenceSignal): string {
  const base = SIGNAL_TEXT[signal.signal] ?? capitalise(signal.signal.replaceAll('_', ' '));
  const detail = signal.detail?.trim();
  return detail ? `${base} (${detail})` : base;
}

/** "+20", "-15" or "0" points. */
export function signalWeight(weight: number): string {
  if (weight > 0) return `+${weight}`;
  if (weight < 0) return `−${Math.abs(weight)}`;
  return '0';
}

export const STATUS_LABELS: Record<FeedbackStatus, string> = {
  draft: 'Draft',
  new: 'New',
  triaging: 'Triaging',
  closed: 'Closed',
};

const AUTOMATION_LABELS: Record<
  FeedbackAutomationState | FeedbackAutomationEventOnlyState,
  string
> = {
  queued: 'Fix queued',
  dispatched: 'Sent to the coding agent',
  investigating: 'Agent investigating',
  pr_opened: 'Pull request open',
  needs_human: 'Needs a human',
  failed: 'Fix failed',
  not_sent: 'Not sent to Code',
};

export function automationLabel(state: string): string {
  return (
    AUTOMATION_LABELS[state as keyof typeof AUTOMATION_LABELS] ??
    capitalise(state.replaceAll('_', ' '))
  );
}

export function automationBadgeVariant(
  state: string
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (state === 'pr_opened') return 'default';
  if (state === 'failed' || state === 'needs_human') return 'destructive';
  // Rudi's settings kept the ticket back on purpose; nothing went wrong.
  if (state === 'not_sent') return 'outline';
  return 'secondary';
}

/** A fix can start when none runs: never dispatched, handed back, or failed. */
export function canStartFix(entry: Pick<Feedback, 'automationState'>): boolean {
  const state = entry.automationState ?? null;
  return state === null || state === 'needs_human' || state === 'failed';
}

/** Readable text for a failed `POST /cortex/feedback/{id}/dispatch`. */
export function dispatchErrorText(error: unknown): string {
  const response = (
    error as { response?: { status?: number; data?: { error?: unknown; detail?: unknown } } }
  )?.response;
  const status = response?.status;
  if (status === 409) return 'A fix is already in progress for this ticket.';
  if (status === 503) return 'Automatic fixes are not set up on this Rudi.';
  if (status === 405 || status === 501) return "This Rudi version can't dispatch fixes yet.";
  if (status === 404) {
    // Rudi answers an unknown ticket with {"error": ...}; a Rudi without the
    // route answers with the framework's plain {"detail": "Not Found"}.
    if (typeof response?.data?.error === 'string')
      return 'This ticket no longer exists. Refresh the list.';
    return "This Rudi version can't dispatch fixes yet.";
  }
  if (status === 403) return 'Rudi did not allow this. Starting a fix needs admin:system.';
  if (status === 400) return "Draft tickets can't be sent yet; wait until the ticket is filed.";
  if (status === 422) {
    const detail = response?.data?.detail ?? response?.data?.error;
    if (typeof detail === 'string' && detail.trim()) return capitalise(detail.trim());
    return 'Rudi held this ticket back because it may contain patient data.';
  }
  const message = response?.data?.error ?? response?.data?.detail;
  if (typeof message === 'string' && message) return capitalise(message);
  return error instanceof Error ? error.message : 'Could not start the fix';
}

const REASON_TEXT: Record<string, string> = {
  patient_data: 'Rudi held the ticket back because it may contain patient data',
  patient_data_guard: 'Rudi held the ticket back because it may contain patient data',
  screenshots: 'Rudi held the ticket back so a person checks its screenshots first',
  delivery_failed: 'Code did not take the ticket',
  no_thread: 'Code took the ticket, but no fix thread reported back within 30 minutes',
  stalled: 'The fix thread sent no report for 24 hours',
};

/** Why Rudi did not send a ticket to Code, for a `not_sent` event without a note. */
const NOT_SENT_REASON_TEXT: Record<string, string> = {
  disabled: 'Automatic fixes are switched off',
  not_configured: 'No Code target is set up',
  category: 'Automatic fixes are off for this category',
  confidence: 'The ticket confidence is below the threshold',
};

const KIND_TEXT: Record<string, string> = {
  email: 'email addresses',
  date: 'dates',
  patient_name: 'patient names',
  phone: 'phone numbers',
  insurance_number: 'insurance numbers',
  labelled_identifier: 'labelled identifiers',
  address: 'addresses',
};

/** "dates", "email addresses and dates", "a, b and c"; unknown kinds read too. */
export function kindsText(kinds: readonly string[]): string {
  const words = [...new Set(kinds.map(kind => KIND_TEXT[kind] ?? kind.replaceAll('_', ' ')))];
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words.at(-1)}`;
}

/**
 * Why Rudi stopped the fix itself, in plain words, with the data types it
 * found. A reason code Rudi has not taught Cortex yet reads as words. A
 * `not_sent` event's note already says why, so it gets no second line.
 */
export function eventReasonText(
  event: Pick<FeedbackAutomationEvent, 'reason' | 'kinds'> &
    Partial<Pick<FeedbackAutomationEvent, 'state' | 'note'>>
): string | null {
  const reason = event.reason?.trim();
  const kinds = (event.kinds ?? []).filter(kind => kind.trim());
  if (!reason && kinds.length === 0) return null;
  if (event.state === 'not_sent' && kinds.length === 0) {
    if (event.note?.trim()) return null;
    if (reason && NOT_SENT_REASON_TEXT[reason]) return NOT_SENT_REASON_TEXT[reason];
  }
  const base = reason
    ? (REASON_TEXT[reason] ??
      capitalise(/^[a-z0-9_]+$/.test(reason) ? reason.replaceAll('_', ' ') : reason).replace(
        /\.$/,
        ''
      ))
    : 'Rudi stopped the fix';
  return kinds.length > 0 ? `${base} (found: ${kindsText(kinds)})` : base;
}

/** The URL when it is http(s); anything else (javascript:, data:) gets no link. */
export function safeHttpUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

/** The branch on GitHub, from the repository of a pull request URL. */
export function branchUrl(branch: string, prUrl: string | null | undefined): string | null {
  const match = safeHttpUrl(prUrl)?.match(/^(https:\/\/github\.com\/[^/]+\/[^/]+)\/pull\/\d+/);
  return match ? `${match[1]}/tree/${branch.split('/').map(encodeURIComponent).join('/')}` : null;
}

/** "820 B", "14 KB", "2.4 MB". */
export function formatBytes(size: number): string {
  if (!Number.isFinite(size) || size < 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

/** For QueryState: a Rudi without drafts rejects `status=draft` as invalid. */
export function feedbackLoadError(error: unknown, status: string): Error | null {
  if (!error) return null;
  const code = (error as { response?: { status?: number } })?.response?.status;
  if (status === 'draft' && code === 422)
    return new Error('This Rudi release does not keep draft tickets.');
  return error instanceof Error ? error : new Error('Failed to load');
}

/** Search for the Replays page: the session, and the window to start at. */
export function replaySearch(
  entry: Pick<Feedback, 'replaySessionId' | 'replayWindowStart' | 'replayWindowEnd'>
): { session: string; from?: string; to?: string } | null {
  if (!entry.replaySessionId) return null;
  return {
    session: entry.replaySessionId,
    ...(entry.replayWindowStart ? { from: entry.replayWindowStart } : {}),
    ...(entry.replayWindowEnd ? { to: entry.replayWindowEnd } : {}),
  };
}

// ── Automatic fix settings ───────────────────────────────────────────

export const AUTOMATION_UNAVAILABLE = "This Rudi version can't manage automatic fixes yet.";

/** The ticket categories the Support agent files, in Rudi's order. */
export const FEEDBACK_CATEGORIES = [
  'bug',
  'improvement',
  'feature_request',
  'performance',
  'ux',
  'documentation',
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  bug: 'Bug',
  improvement: 'Improvement',
  feature_request: 'Feature request',
  performance: 'Performance',
  ux: 'UX',
  documentation: 'Documentation',
};

export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? capitalise(category.replaceAll('_', ' '));
}

/**
 * The categories Rudi offers (or the known ones from an older Rudi) plus any
 * it already has selected, so saving keeps them.
 */
export function categoryChoices(
  selected: readonly string[],
  available: readonly string[] = FEEDBACK_CATEGORIES
): string[] {
  return [...new Set<string>([...available, ...selected])];
}

/** Where tickets go now, in words: a target's name, the environment's host, or nowhere. */
export function destinationText(
  config: Pick<FeedbackAutomationConfig, 'source' | 'environmentHost' | 'targets'>
): string {
  if (config.source === 'cortex') {
    const active = config.targets.find(target => target.active);
    if (active) return `${active.name} (${active.url})`;
  }
  if (config.source === 'environment') {
    return config.environmentHost
      ? `the environment fallback (${config.environmentHost})`
      : 'the environment fallback';
  }
  return 'nowhere';
}

/** True for an absolute https URL with a host; Rudi refuses anything else. */
export function isHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' && !!url.hostname;
  } catch {
    return false;
  }
}

/** A whole number from 0 to 100, or null. */
export function parseThreshold(value: string): number | null {
  const text = value.trim();
  if (!/^\d{1,3}$/.test(text)) return null;
  const number = Number(text);
  return number <= 100 ? number : null;
}

export type AutomationWrite = 'settings' | 'reset' | 'add' | 'edit' | 'active' | 'delete';

const WRITE_FALLBACK: Record<AutomationWrite, string> = {
  settings: 'Could not save the settings',
  reset: 'Could not reset the settings',
  add: 'Could not add the target',
  edit: 'Could not save the target',
  active: 'Could not switch the target',
  delete: 'Could not delete the target',
};

function errorMessage(data: { error?: unknown; detail?: unknown } | undefined): string | null {
  const message = data?.error ?? data?.detail;
  if (typeof message === 'string' && message.trim()) return capitalise(message.trim());
  // FastAPI validation errors: [{"msg": "..."}].
  if (Array.isArray(message)) {
    const first = (message[0] as { msg?: unknown } | undefined)?.msg;
    if (typeof first === 'string' && first.trim()) return capitalise(first.trim());
  }
  return null;
}

/** Readable text for a failed write to `/cortex/feedback-automation/*`. */
export function automationWriteErrorText(error: unknown, write: AutomationWrite): string {
  const response = (
    error as { response?: { status?: number; data?: { error?: unknown; detail?: unknown } } }
  )?.response;
  const status = response?.status;
  const rudiError = typeof response?.data?.error === 'string';
  if (status === 405 || status === 501) return AUTOMATION_UNAVAILABLE;
  if (status === 404) {
    // Rudi answers an unknown target with {"error": ...}; a Rudi without the
    // route answers with the framework's plain {"detail": "Not Found"}.
    return rudiError ? 'This target no longer exists.' : AUTOMATION_UNAVAILABLE;
  }
  if (status === 409) {
    if (write === 'delete')
      return "The active target can't be deleted. Make another one active first.";
    if (write === 'settings')
      return 'Someone else changed these settings. The page now shows their version; check it and save again.';
    return 'Someone else changed this at the same time. Check the page and try again.';
  }
  if (status === 503) return "This Rudi can't encrypt secrets, so it can't store targets.";
  if (status === 403) return 'Rudi did not allow this change.';
  if (status === 422) {
    if (write === 'add' || write === 'edit')
      return errorMessage(response?.data) ?? 'Enter an https URL and a secret.';
    return errorMessage(response?.data) ?? 'Rudi refused these values.';
  }
  return errorMessage(response?.data) ?? WRITE_FALLBACK[write];
}

function capitalise(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}
